import React, {
  DetailedHTMLProps,
  ButtonHTMLAttributes,
  ReactNode,
  forwardRef,
  useMemo,
} from 'react';
import { useFocusRing } from '@react-aria/focus';
import { mergeProps } from '@react-aria/utils';
import classnames from 'classnames';
import {
  ChevronDownLgIcon as ChevronDownIcon,
  ChevronUpLgIcon as ChevronUpIcon,
} from '@stpkg/stand-react';

export type RSBorderedListItemCollapsibleProps = DetailedHTMLProps<
  ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
> & {
  leftContent: ReactNode;
  rightContent?: ReactNode;
  additionalContent?: ReactNode;
  details: ReactNode;
  isCollapsed: boolean;
  isSelected?: boolean; // details内の要素が選択状態の時などに用いる
};

export const RSBorderedListItemCollapsible = forwardRef<
  HTMLButtonElement,
  RSBorderedListItemCollapsibleProps
>(
  (
    {
      leftContent,
      rightContent,
      additionalContent,
      details,
      isCollapsed,
      isSelected,
      ...props
    },
    ref,
  ) => {
    const { isFocusVisible, focusProps } = useFocusRing();

    const color = '#0066FF';
    const colorTint = '#F1F7FF';

    const buttonRingSizeClasses = useMemo(() => {
      if (isFocusVisible) {
        return ['ring-2'];
      }

      if (isSelected) {
        return ['ring-1'];
      }

      return [];
    }, [isSelected, isFocusVisible]);

    return (
      <>
        <li
          className={classnames(
            'group',
            'list-style-none',
            'box-border',
            'block',
            'relative',
            'border-b',
            'border-t-0',
            'border-r-0',
            'border-l-0',
            'border-solid',
            'border-gray-200',
            'first-of-type:rounded-t-10',
            'last-of-type:rounded-b-10',
            'last-of-type:border-b-0',
          )}
        >
          <button
            {...mergeProps(props, focusProps)}
            ref={ref}
            type="button"
            className={classnames(
              'button',
              isSelected && 'selected',
              isFocusVisible && 'focus-visible',
              'appearance-none',
              'box-border',
              'relative',
              'block',
              'w-full',
              'p-2.5',
              'text-left',
              'bg-white',
              'border-none',
              'outline-none',
              'focus:outline-none',
              buttonRingSizeClasses,
              (isSelected || isFocusVisible) && 'z-1',
              'transition',
              'cursor-pointer',
              'group-first-of-type:rounded-t-10',
              isCollapsed && 'group-last-of-type:rounded-b-10',
            )}
          >
            <div className="flex items-center justify-between">
              <div className="flex w-full items-center justify-between">
                <div>{leftContent}</div>
                {rightContent && <div>{rightContent}</div>}
              </div>

              <div className="ml-1.5 flex-none">
                <div className="block h-[20px] w-[20px]">
                  {isCollapsed ? (
                    <ChevronDownIcon
                      className="block fill-gray-400"
                      width="20"
                      height="20"
                      viewBox="0 0 48 48"
                    />
                  ) : (
                    <ChevronUpIcon
                      className="block fill-gray-400"
                      width="20"
                      height="20"
                      viewBox="0 0 48 48"
                    />
                  )}
                </div>
              </div>
            </div>

            {additionalContent && (
              <div className="mt-1">{additionalContent}</div>
            )}
          </button>

          <div
            className={classnames(
              isCollapsed && 'hidden',
              'bg-gray-10',
              'border-t',
              'border-solid',
              'border-gray-200',
              'border-r-0',
              'border-l-0',
              'border-b-0',
              'group-last-of-type:rounded-b-10',
            )}
          >
            {details}
          </div>
        </li>

        <style jsx>{`
          .button:hover {
            background-color: ${colorTint};
          }

          .button.focus-visible {
            --tw-ring-color: ${color};
          }

          .button.selected {
            --tw-ring-color: ${color};
            background-color: ${colorTint};
          }
        `}</style>
      </>
    );
  },
);

RSBorderedListItemCollapsible.displayName = 'RSBorderedListItemCollapsible';
